import { useEffect, useState } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../utils/api";
import { getIconComponent } from "../data/icons";
import IconPicker from "./IconPicker";
import { usePraxis } from "../context/PraxisContext";

export default function ServicesManager() {
  const { refresh } = usePraxis();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const reload = async () => {
    setLoading(true);
    const { data } = await api.listServices();
    setLoading(false);
    if (data && data.ok) setItems(data.items || []);
    else setError("Konnte Leistungen nicht laden.");
  };
  useEffect(() => { reload(); }, []);

  const startNew = () => setEditing({
    id: null, slug: "", title: "", icon: "stethoscope", short: "",
    bullets: [], note: "", has_page: false, page_path: "",
    is_new: false, new_label: "",
  });
  const startEdit = (it) => setEditing({ ...it, bullets: [...(it.bullets || [])] });
  const cancel = () => { setEditing(null); setError(""); setMessage(""); };

  const save = async (item) => {
    setError(""); setMessage("");
    if (!item.title.trim()) { setError("Bitte einen Titel angeben."); return; }
    const payload = {
      title: item.title.trim(),
      icon: item.icon || "stethoscope",
      short: item.short || "",
      bullets: (item.bullets || []).map((b) => b.trim()).filter(Boolean),
      note: item.note || "",
      has_page: !!item.has_page,
      page_path: item.page_path || "",
      is_new: !!item.is_new,
      new_label: item.new_label || "",
    };
    if (item.slug) payload.slug = item.slug;

    const { data } = item.id
      ? await api.updateService(item.id, payload)
      : await api.createService(payload);
    if (data && data.ok) {
      setMessage(item.id ? "Leistung aktualisiert." : "Leistung angelegt.");
      setEditing(null);
      await reload();
      refresh(); // Homepage-Cache neu laden
    } else {
      setError((data && data.message) || "Speichern fehlgeschlagen.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Leistung „${it.title}“ wirklich löschen?`)) return;
    const { data } = await api.deleteService(it.id);
    if (data && data.ok) { setMessage("Leistung gelöscht."); await reload(); refresh(); }
  };

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const nextItems = arrayMove(items, oldIdx, newIdx);
    setItems(nextItems);
    await api.reorderServices(nextItems.map((i) => i.id));
    refresh();
  };

  if (editing) {
    return <ServiceForm item={editing} onCancel={cancel} onSave={save} error={error} message={message} />;
  }

  return (
    <div data-testid="services-manager">
      <div className="admin-list-header">
        <h2 className="admin-list-title">{items.length} Leistung{items.length === 1 ? "" : "en"}</h2>
        <button type="button" onClick={startNew} className="admin-btn admin-btn-primary" data-testid="service-new">
          + Neue Leistung
        </button>
      </div>

      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      {loading ? <p>Lade…</p> : items.length === 0 ? (
        <p className="admin-empty">Noch keine Leistungen angelegt.</p>
      ) : (
        <>
          <p className="admin-align-hint" style={{ margin: "0 0 0.75rem" }}>
            Die Reihenfolge bestimmt die Anzeige auf der Startseite. Ziehe die Zeilen zum Umsortieren.
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <ul className="team-list" data-testid="services-list">
                {items.map((it) => (
                  <SortableServiceRow key={it.id} item={it} onEdit={() => startEdit(it)} onDelete={() => remove(it)} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </>
      )}
    </div>
  );
}

function SortableServiceRow({ item, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const Icon = getIconComponent(item.icon);

  return (
    <li ref={setNodeRef} style={style} className="team-list-item" data-testid={`service-item-${item.id}`}>
      <span className="team-drag-handle" {...attributes} {...listeners} title="Ziehen zum Sortieren" data-testid={`service-drag-${item.id}`}>
        <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor" aria-hidden="true">
          <circle cx="4" cy="4" r="1.5"/><circle cx="10" cy="4" r="1.5"/>
          <circle cx="4" cy="10" r="1.5"/><circle cx="10" cy="10" r="1.5"/>
          <circle cx="4" cy="16" r="1.5"/><circle cx="10" cy="16" r="1.5"/>
        </svg>
      </span>
      <div className="service-icon-thumb" aria-hidden="true">
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <div className="team-info">
        <div className="team-name">{item.title}</div>
        <div className="team-position">
          {item.short ? item.short.slice(0, 90) + (item.short.length > 90 ? "…" : "") : <em>ohne Beschreibung</em>}
          {item.is_new && <span className="admin-status-badge status-active" style={{ marginLeft: 8, fontSize: '0.72rem' }}>NEU</span>}
        </div>
      </div>
      <div className="team-actions">
        <button type="button" onClick={onEdit} className="admin-btn admin-btn-ghost" data-testid={`service-edit-${item.id}`}>Bearbeiten</button>
        <button type="button" onClick={onDelete} className="admin-btn admin-btn-danger" data-testid={`service-delete-${item.id}`}>Löschen</button>
      </div>
    </li>
  );
}

function ServiceForm({ item, onCancel, onSave, error, message }) {
  const [current, setCurrent] = useState(item);
  const [newBullet, setNewBullet] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (patch) => setCurrent({ ...current, ...patch });
  const addBullet = () => {
    const b = newBullet.trim();
    if (!b) return;
    set({ bullets: [...(current.bullets || []), b] });
    setNewBullet("");
  };
  const removeBullet = (idx) => set({ bullets: current.bullets.filter((_, i) => i !== idx) });
  const moveBullet = (idx, dir) => {
    const b = [...current.bullets];
    const j = idx + dir;
    if (j < 0 || j >= b.length) return;
    [b[idx], b[j]] = [b[j], b[idx]];
    set({ bullets: b });
  };
  const editBullet = (idx, val) => {
    const b = [...current.bullets];
    b[idx] = val;
    set({ bullets: b });
  };

  const doSave = async () => { setSaving(true); await onSave(current); setSaving(false); };

  return (
    <div className="admin-form" data-testid="service-form">
      <h2 className="admin-list-title">{item.id ? "Leistung bearbeiten" : "Neue Leistung anlegen"}</h2>
      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      <div className="admin-two-col">
        <label className="admin-label">
          Titel
          <input type="text" value={current.title} onChange={(e) => set({ title: e.target.value })}
                 required maxLength={100} data-testid="service-title-input"/>
        </label>
        <label className="admin-label">
          Icon
          <IconPicker value={current.icon} onChange={(v) => set({ icon: v })} />
        </label>
      </div>

      <label className="admin-label">
        Kurzbeschreibung
        <textarea value={current.short} onChange={(e) => set({ short: e.target.value })}
                  rows={3} maxLength={400} data-testid="service-short-input"/>
      </label>

      <label className="admin-label">
        Aufzählungspunkte (optional)
        <ul className="bullet-editor" data-testid="service-bullets-list">
          {(current.bullets || []).map((b, i) => (
            <li key={i} className="bullet-editor-row" data-testid={`service-bullet-${i}`}>
              <input type="text" value={b} onChange={(e) => editBullet(i, e.target.value)} data-testid={`service-bullet-input-${i}`}/>
              <button type="button" onClick={() => moveBullet(i, -1)} className="admin-btn admin-btn-ghost" title="hoch" data-testid={`service-bullet-up-${i}`}>↑</button>
              <button type="button" onClick={() => moveBullet(i, 1)} className="admin-btn admin-btn-ghost" title="runter" data-testid={`service-bullet-down-${i}`}>↓</button>
              <button type="button" onClick={() => removeBullet(i)} className="admin-btn admin-btn-danger" title="entfernen" data-testid={`service-bullet-remove-${i}`}>×</button>
            </li>
          ))}
        </ul>
        <div className="bullet-editor-add">
          <input type="text" placeholder="Neuen Punkt hinzufügen…"
                 value={newBullet} onChange={(e) => setNewBullet(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBullet(); } }}
                 data-testid="service-bullet-new-input"/>
          <button type="button" onClick={addBullet} className="admin-btn admin-btn-primary" data-testid="service-bullet-add">+ Hinzufügen</button>
        </div>
      </label>

      <label className="admin-label">
        Zusatzhinweis (optional, kleiner Text unter den Bullets)
        <textarea value={current.note} onChange={(e) => set({ note: e.target.value })}
                  rows={2} maxLength={400} data-testid="service-note-input"/>
      </label>

      <div className="admin-two-col">
        <label className="admin-label admin-checkbox-label">
          <input type="checkbox" checked={!!current.is_new}
                 onChange={(e) => set({ is_new: e.target.checked })}
                 data-testid="service-isnew-input"/>
          Als „Neu“ markieren (Badge auf der Karte)
        </label>
        <label className="admin-label" style={{ opacity: current.is_new ? 1 : 0.4 }}>
          Text auf dem Badge
          <input type="text" value={current.new_label}
                 onChange={(e) => set({ new_label: e.target.value })}
                 disabled={!current.is_new} maxLength={40} placeholder="z. B. Neu"
                 data-testid="service-newlabel-input"/>
        </label>
      </div>

      <div className="admin-two-col">
        <label className="admin-label admin-checkbox-label">
          <input type="checkbox" checked={!!current.has_page}
                 onChange={(e) => set({ has_page: e.target.checked })}
                 data-testid="service-haspage-input"/>
          „Mehr erfahren“-Link zeigen
        </label>
        <label className="admin-label" style={{ opacity: current.has_page ? 1 : 0.4 }}>
          Ziel-Pfad (z. B. „wechseljahre“)
          <input type="text" value={current.page_path}
                 onChange={(e) => set({ page_path: e.target.value.replace(/^\/+/, "") })}
                 disabled={!current.has_page} maxLength={80}
                 data-testid="service-pagepath-input"/>
        </label>
      </div>

      <div className="admin-actions-row">
        <button type="button" onClick={doSave} className="admin-btn admin-btn-primary" disabled={saving} data-testid="service-save">
          {saving ? "Speichere…" : (item.id ? "Änderungen speichern" : "Anlegen")}
        </button>
        <button type="button" onClick={onCancel} className="admin-btn admin-btn-ghost" data-testid="service-cancel">
          Abbrechen
        </button>
      </div>
    </div>
  );
}
